import { useContext, useState, useEffect } from "react";
import { ChartContext } from "../../../Context/ChartContext/ChartContext";
import MainContext from "../../../Context/MainContext/MainContext";
import moment from "moment";
import Card from "../../Card";
import MUIBox from "@mui/material/Box";
import MUIGrid from "@mui/material/Grid";
import MUITypography from "@mui/material/Typography";
import MUITextField from "@mui/material/TextField";
import MUIButton from "@mui/material/Button";
import MUIDrawIcon from "@mui/icons-material/Draw";
import MUIAddIcon from "@mui/icons-material/Add";

import { DatePicker as MUIDatePicker } from '@mui/x-date-pickers/DatePicker';

/**
 * Renders a Card Signature component.
 * @returns {JSX.Element}
 */
const className = "CardChartSignature";
const title = "Signature";
const icon = MUIDrawIcon;

export default function CardChartSignature(props) {
    const chartContext = useContext(ChartContext);
    const mainContext = useContext(MainContext);

    const [signed, setSigned] = useState(null);
    const [newDocumentSignatureId, setNewDocumentSignatureId] = useState(null);

    const atpAssessment = Object.values(chartContext.chart.atpAssessment).find((atpAssessment) => {
        return atpAssessment.orderActivityId === chartContext.orderId;
    }) || [];

    const signatures = Object.values(mainContext.signaturesIndexed).filter(sig => sig.signatureTypeTermId === 130);
    const hasSignature = signatures.length > 0 ? signatures[signatures.length - 1] : null;

    const assistiveTechnologyReportActivityFileTypeId = Object.values(mainContext.termsIndexed || {}).find(term => term.type === 'activity_file_type' && term.name === 'Assistive Technology Report')?.id || null;

    // Set validation rules for the document signature
    useEffect(() => {
        if (newDocumentSignatureId) {
            chartContext.updateValidationRules({
                key: className,
                rules: [
                    {
                        target: "documentSignature",
                        id: newDocumentSignatureId,
                        field: "signatureId",
                        rule: {
                            "required": true
                        }
                    },
                ]
            });
        } else {
            chartContext.updateValidationRules({ key: className, rules: [] });
        }

        // Cleanup: reset validation rules when component unmounts
        return () => {
            chartContext.updateValidationRules({ key: className, rules: [] });
        };
    }, [newDocumentSignatureId]);


    // Set a new document signature id when the component mounts
    useEffect(() => {
        const newDocumentSignatureId = chartContext.getNegativeId();
        setNewDocumentSignatureId(newDocumentSignatureId);
    }, []);

    // Create a new document signature object
    useEffect(() => {
        if (!newDocumentSignatureId) {
            return;
        }

        chartContext.onChange({
            documentSignature: {
                [newDocumentSignatureId]: {
                    id: newDocumentSignatureId,
                    createdByUserprofileId: mainContext.currentUser.id,
                    documentSignatureTypeTermId: 134, //"ATP Assessment - Signature" (Dependant on hasSignature values)
                    orderActivityId: chartContext.orderId,
                    patientId: chartContext.patientId,
                    tableName: "atp_assessment",
                    rowId: atpAssessment.id,
                    signatureId: null,
                }
            },
        })
    }, [newDocumentSignatureId]);

    //TODO: This also needs to upload atp assessment document in CardChartPreview or on page load to attachments table, set up object automatically
    const text = props?.text || "";
    const buttons = signed ? [] : (hasSignature ? [
        <MUIButton
            key="sign-as-button"
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<MUIDrawIcon />}
            onClick={() => {
                let signature = hasSignature.data;
                setSigned(signature)
                chartContext.onChange({ documentSignature: { [newDocumentSignatureId]: { signatureId: hasSignature.id } } })
            }}
            sx={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}>
            Sign as {mainContext.currentUser.firstname} {mainContext.currentUser.lastname}
        </MUIButton>,
        <MUIButton
            key="manage-signature-button"
            variant="text"
            color="primary"
            size="small"
            onClick={() => {
                chartContext.updateShowManageSignatureDialog(true);
            }}
            sx={{ whiteSpace: 'nowrap', minWidth: 'fit-content', ml: 1 }}>
            Manage Signature
        </MUIButton>
    ] : [
        <MUIButton
            key="create-new-signature-button"
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<MUIAddIcon />}
            onClick={() => {
                chartContext.updateShowManageSignatureDialog(true);
            }}
            sx={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}>
            Create New Signature
        </MUIButton>
    ])

    /**
     * Renders the content of the card.
     * @returns {JSX.Element} MUIBox component for card content
     */
    const renderContent = () => (

        <MUIBox>
            <MUITypography variant="body2" sx={{ mb: 2 }}>
                {text}
            </MUITypography>
            <MUIGrid container spacing={3}>
                <MUIGrid item xs={12} lg={9}>
                    <MUITextField
                        label={"ATP Signature"}
                        fullWidth={true}
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                        disabled={signed ? true : false}
                        sx={!signed ? {
                            '& .MuiInputBase-root': {
                                paddingTop: '8px',
                                paddingBottom: '6px',
                            }
                        } : {}}
                        InputProps={{
                            startAdornment: signed && signed.trim().startsWith('<svg') ? (
                                <MUIBox
                                    dangerouslySetInnerHTML={{ __html: signed }}
                                    sx={{
                                        '& svg': {
                                            maxWidth: '300px',
                                            height: '53px',
                                            display: 'block'
                                        },
                                        '& svg path': {
                                            stroke: (theme) => theme.palette.primary.main
                                        }
                                    }}
                                />
                            ) : (
                                buttons
                            ),
                        }}
                        value={signed && !signed.trim().startsWith('<svg') ? signed : ""}
                        required={true}
                        error={chartContext.validationIssues[className]?.documentSignature?.[newDocumentSignatureId]?.signatureId ? true : false}
                        helperText={chartContext.validationIssues[className]?.documentSignature?.[newDocumentSignatureId]?.signatureId ? chartContext.validationIssues[className]?.documentSignature?.[newDocumentSignatureId]?.signatureId : ""}
                    />
                    {signed ? (
                        <MUIButton
                            variant="text"
                            color="error"
                            size="small"
                            sx={{ padding: "0px !important", alignSelf: "flex-start", mt: 1 }}
                            onClick={() => {
                                setSigned(null);
                                chartContext.onChange({ documentSignature: { [newDocumentSignatureId]: { signatureId: null } } })
                            }}
                        >
                            Clear Signature
                        </MUIButton>
                    ) : null}
                </MUIGrid>
                <MUIGrid item xs={12} lg={3}>
                    <MUIDatePicker
                        label="Date"
                        value={moment(new Date())}
                        readOnly={true}
                        disabled={true}
                        slotProps={{
                            field: {
                                clearable: true,
                            },
                            textField: {
                                fullWidth: true,
                                size: "small",
                                variant: 'standard',
                                required: true,
                                InputLabelProps: {
                                    shrink: true
                                },
                                sx: signed && signed.trim().startsWith('<svg') ? {
                                    '& .MuiInputBase-root': {
                                        minHeight: '53px',
                                        alignItems: 'flex-end',
                                    },
                                    '& .MuiInputAdornment-root': {
                                        alignSelf: 'flex-end',
                                        marginBottom: '16px',
                                        marginRight: '4px',
                                    }
                                } : signed && !signed.trim().startsWith('<svg') ? {
                                    '& .MuiInputBase-root': {
                                        minHeight: '32px',
                                    }
                                } : {
                                    '& .MuiInputBase-root': {
                                        minHeight: '46px',
                                        alignItems: 'flex-end',
                                    },
                                    '& .MuiInputAdornment-root': {
                                        alignSelf: 'flex-end',
                                        marginBottom: '16px',
                                    }
                                }
                            },
                        }}
                        views={['year', 'month', 'day']}
                    />
                </MUIGrid>
            </MUIGrid>
        </MUIBox>
    );

    return (
        <Card
            renderContent={renderContent}
            getTitle={() => title}

        />
    );
}

CardChartSignature.meta = { className: className, title: title, icon: icon };
